import React from "react";
import { Search, PlaneLanding, PlaneTakeoff, Plane, Crosshair } from "lucide-react";
import { flightLogoUrl } from "../../lib/api";

const FRIENDLY = {
  LND: "Landed", LANDED: "Landed", ARR: "Landed", ARRIVED: "Landed",
  ON: "On time", SCH: "Scheduled", EXP: "Expected", UNK: "Scheduled",
  DEP: "Departed", DEPARTED: "Departed", AIR: "En route",
  PTG: "Boarding", BOARDING: "Boarding", GTO: "Gate open", GCL: "Gate closed",
  FNL: "Final call", CKI: "Check-in", CKO: "Check-in open",
  DLY: "Delayed", DELAYED: "Delayed",
  CNL: "Cancelled", CANCELLED: "Cancelled", CXL: "Cancelled",
};

function statusStyle(label, state) {
  const l = (label || "").toLowerCase();
  if (state === "danger" || l.includes("cancel")) return { c: "#FF7089", bg: "rgba(255,92,122,0.16)" };
  if (state === "warn" || l.includes("delay")) return { c: "#FFC726", bg: "rgba(255,199,38,0.16)" };
  if (l.includes("land") || l.includes("depart") || l.includes("on time")) return { c: "#22E0A1", bg: "rgba(34,224,161,0.16)" };
  if (l.includes("board") || l.includes("gate") || l.includes("final") || l.includes("check")) return { c: "#5B9BFF", bg: "rgba(91,155,255,0.16)" };
  return { c: "#93A4C0", bg: "rgba(255,255,255,0.06)" };
}

const Logo = ({ f }) => {
  const [err, setErr] = React.useState(false);
  const show = f.logo && f.logo !== "default.jpg" && !err;
  if (show) return <img src={flightLogoUrl(f.logo)} alt="" className="h-6 w-6 rounded-md bg-white/90 object-contain p-0.5" loading="lazy" onError={() => setErr(true)} />;
  return <span className="flex h-6 w-6 items-center justify-center rounded-md bg-white/10"><Plane size={13} color="#7C8DA8" /></span>;
};

const FlightsBoard = ({ flights, onTrackFlight, watched, onWatch }) => {
  const [tab, setTab] = React.useState("arrivals");
  const [q, setQ] = React.useState("");

  const rows = (tab === "arrivals" ? flights.arrivals : flights.departures) || [];
  const filtered = rows.filter((r) => {
    if (!q.trim()) return true;
    return `${r.flight} ${r.other} ${r.airline || ""}`.toLowerCase().includes(q.trim().toLowerCase());
  });

  return (
    <div data-testid="flights-board">
      <div className="mb-3 flex rounded-2xl bg-white/[0.06] p-1">
        <button onClick={() => setTab("arrivals")} data-testid="flights-tab-arrivals"
          className={`flex flex-1 items-center justify-center gap-1.5 rounded-xl py-2 text-sm font-bold transition-colors ${tab === "arrivals" ? "bg-[#1E4DB7] text-white" : "text-[#8194B2]"}`}>
          <PlaneLanding size={16} /> Arrivals
        </button>
        <button onClick={() => setTab("departures")} data-testid="flights-tab-departures"
          className={`flex flex-1 items-center justify-center gap-1.5 rounded-xl py-2 text-sm font-bold transition-colors ${tab === "departures" ? "bg-[#1E4DB7] text-white" : "text-[#8194B2]"}`}>
          <PlaneTakeoff size={16} /> Departures
        </button>
      </div>

      <div className="go-card mb-3 flex items-center gap-2 rounded-2xl px-3.5 py-2.5">
        <Search size={17} color="#67799A" />
        <input value={q} onChange={(e) => setQ(e.target.value)} placeholder={`Search ${tab} by flight or city`}
          className="w-full bg-transparent text-sm font-medium text-white outline-none placeholder:text-[#5f7193]" data-testid="flights-search" />
      </div>

      <div className="go-card overflow-hidden rounded-3xl">
        {filtered.length === 0 && (
          <div className="px-4 py-8 text-center text-sm text-[#67799A]" data-testid="flights-empty">No {tab} match your search.</div>
        )}
        {filtered.map((f, i) => {
          const label = FRIENDLY[(f.status || "").toUpperCase()] || f.status || "Scheduled";
          const st = statusStyle(label, f.state);
          const delayed = f.est && f.time && f.est !== f.time;
          const isWatched = watched && watched.flight === f.flight;
          return (
            <div key={i} className={`flex items-center gap-3 px-4 py-3 ${i ? "border-t border-white/[0.06]" : ""}`} data-testid={`flight-${tab}-${i}`}>
              <Logo f={f} />
              <button onClick={() => onTrackFlight && onTrackFlight(f)} className="min-w-0 flex-1 text-left" data-testid={`flight-track-${tab}-${i}`}>
                <div className="font-head text-sm font-bold text-white">{f.flight} <span className="font-sans text-xs font-medium text-[#67799A]">{f.airline}</span></div>
                <div className="truncate text-xs text-[#8194B2]">{tab === "arrivals" ? "From" : "To"} {f.other}{f.gate && f.gate !== "--" ? ` · Gate ${f.gate}` : ""}</div>
              </button>
              <div className="shrink-0 text-right">
                <div className="font-head text-sm font-bold text-[#D8E3F5]">
                  {delayed ? <><span className="mr-1 text-[#5f7193] line-through">{f.time}</span>{f.est}</> : f.time}
                </div>
                <span className="mt-0.5 inline-block rounded-full px-2 py-0.5 text-[10px] font-bold" style={{ background: st.bg, color: st.c }}>{label}</span>
              </div>
              <button onClick={() => onWatch && onWatch(isWatched ? null : { flight: f.flight, kind: tab, other: f.other, status: f.status })}
                className="shrink-0 rounded-full p-1.5 active:scale-90" data-testid={`flight-watch-${tab}-${i}`} title="Watch this flight">
                <Crosshair size={16} color={isWatched ? "#FFC726" : "#3E4E6B"} strokeWidth={2.4} />
              </button>
            </div>
          );
        })}
      </div>
      <p className="mt-2 px-1 text-[11px] text-[#67799A]">Tap a flight to locate it in the airspace · tap the target to watch it.</p>
    </div>
  );
};

export default FlightsBoard;
